import { ModalBody } from 'react-bootstrap';

import useFetchSystemTemplateByKey from 'components/domain/automation/systemTemplates/useFetchSystemTemplateByKey';
import FormWithProvider from 'components/forms/form/Form';
import FormFieldset from 'components/forms/form/FormFieldset';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import { LanguageCode } from 'i18n/internationalization.types';
import { SystemTemplates } from 'models/automation/SystemTemplates';
import useAvailableLanguages from '../../../common/useAvailableLanguages';
import {
  getFormDefaultValues,
  systemTemplateEditModalSchema,
} from './SystemTemplateEditModal.schema';
import SystemTemplateEditModalFooter from './SystemTemplateEditModalFooter';
import SystemTemplateEditModalFormElements from './SystemTemplateEditModalFormElements';
import useSystemTemplateFormSubmit from './useSystemTemplateFormSubmit';

interface SystemTemplateEditModalProps {
  closeModal: () => void;
  systemTemplateKey: SystemTemplates;
}

const SystemTemplateEditModal = ({
  closeModal,
  systemTemplateKey: key,
}: SystemTemplateEditModalProps) => {
  const { availableLanguages, isLoading: isLoadingAvailableLanguages } =
    useAvailableLanguages();

  const { data: systemTemplate, isLoading: isLoadingTemplate } =
    useFetchSystemTemplateByKey({ key });

  const { onSubmit } = useSystemTemplateFormSubmit({
    closeModal,
    systemTemplate,
  });

  if (isLoadingTemplate || isLoadingAvailableLanguages) {
    return <LoadingPlaceholder />;
  }

  if (!systemTemplate) {
    return null;
  }

  return (
    <FormWithProvider
      defaultValues={getFormDefaultValues({
        availableLanguages,
        currentLanguage: LanguageCode.EN,
        systemTemplate,
      })}
      dirtyChecker
      dirtyCheckerOptions={{
        skipFields: ['language'],
      }}
      horizontal
      onSubmit={onSubmit}
      resolver={systemTemplateEditModalSchema({ availableLanguages })}
      noValidate
    >
      <FormFieldset>
        <ModalBody>
          <SystemTemplateEditModalFormElements
            availableLanguages={availableLanguages}
            systemTemplate={systemTemplate}
          />
        </ModalBody>
        <SystemTemplateEditModalFooter
          closeModal={closeModal}
          systemTemplate={systemTemplate}
        />
      </FormFieldset>
    </FormWithProvider>
  );
};

export default SystemTemplateEditModal;
